/**
 * ElationPushDialog Component
 *
 * Dialog for pushing care team and pharmacies to Elation EHR.
 * Replicates the push workflow from the Streamlit app.
 *
 * Features:
 * - Shows current push status (what can be pushed)
 * - Push Care Team: Validated providers with NPI
 * - Push Pharmacies: Up to 2 pharmacies with NCPDP IDs
 * - Shows detailed results after push
 */

import React, { useState, useEffect, useCallback } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Stack,
  Typography,
} from '@mui/material';
import {
  Check as CheckIcon,
  Close as CloseIcon,
  CloudUpload as CloudUploadIcon,
  LocalPharmacy as PharmacyIcon,
  MedicalServices as MedicalServicesIcon,
  Person as PersonIcon,
  Star as StarIcon,
  Warning as WarningIcon,
} from '@mui/icons-material';
import { PatientService } from '../../services/patient.service';
import { toast } from 'react-toastify';
import Helper from '../../utils/helper';

const patientService = new PatientService();

interface ElationPushStatus {
  elation_configured: boolean;
  has_elation_patient_id: boolean;
  elation_patient_id: string | null;
  care_team: {
    validated_with_npi: number;
    total_validated: number;
    can_push: boolean;
  };
  pharmacies: {
    with_ncpdp: number;
    total: number;
    can_push: boolean;
  };
}

interface PushResult {
  member_id?: number;
  provider_id?: number;
  provider_name?: string;
  npi?: string;
  success: boolean;
  message: string;
  contact_created?: boolean;
  already_on_team?: boolean;
}

interface PharmacyPushResult {
  slot: number;
  name: string;
  ncpdp_id: string;
  is_preferred: boolean;
}

interface ElationPushDialogProps {
  patientId: string;
  open: boolean;
  onClose: () => void;
}

export const ElationPushDialog: React.FC<ElationPushDialogProps> = ({
  patientId,
  open,
  onClose,
}) => {
  const [loading, setLoading] = useState(false);
  const [status, setStatus] = useState<ElationPushStatus | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Push states
  const [pushingCareTeam, setPushingCareTeam] = useState(false);
  const [pushingPharmacies, setPushingPharmacies] = useState(false);
  const [careTeamResults, setCareTeamResults] = useState<PushResult[] | null>(null);
  const [pharmacyResults, setPharmacyResults] = useState<PharmacyPushResult[] | null>(null);
  const [careTeamMessage, setCareTeamMessage] = useState<string | null>(null);
  const [pharmacyMessage, setPharmacyMessage] = useState<string | null>(null);

  // Load status
  const loadStatus = useCallback(async () => {
    if (!patientId) return;

    try {
      setLoading(true);
      setError(null);
      const response = await patientService.getElationPushStatus(patientId);
      setStatus(response.data);
    } catch (err: any) {
      setError(err.response?.data?.error?.message || 'Failed to load Elation status');
      setStatus(null);
    } finally {
      setLoading(false);
    }
  }, [patientId]);

  useEffect(() => {
    if (open && patientId) {
      loadStatus();
      // Reset results when dialog opens
      setCareTeamResults(null);
      setPharmacyResults(null);
      setCareTeamMessage(null);
      setPharmacyMessage(null);
    }
  }, [open, patientId, loadStatus]);

  // Push care team
  const handlePushCareTeam = async () => {
    try {
      setPushingCareTeam(true);
      setCareTeamResults(null);
      setCareTeamMessage(null);

      const response = await patientService.pushCareTeamToElation(patientId);
      const data = response.data;

      setCareTeamResults(data.results || []);
      setCareTeamMessage(data.message);

      if (data.success) {
        toast.success(data.message, Helper.bottom_center());
      } else {
        toast.warning(data.message, Helper.bottom_center());
      }

      // Refresh status
      loadStatus();
    } catch (err: any) {
      const errorMsg = err.response?.data?.error?.message || 'Push to Elation failed';
      toast.error(errorMsg, Helper.bottom_center());
      setCareTeamMessage(errorMsg);
    } finally {
      setPushingCareTeam(false);
    }
  };

  // Push pharmacies
  const handlePushPharmacies = async () => {
    try {
      setPushingPharmacies(true);
      setPharmacyResults(null);
      setPharmacyMessage(null);

      const response = await patientService.pushPharmaciesToElation(patientId);
      const data = response.data;

      setPharmacyResults(data.pharmacies || []);
      setPharmacyMessage(data.message);

      if (data.success) {
        toast.success(data.message, Helper.bottom_center());
      } else {
        toast.warning(data.message, Helper.bottom_center());
      }

      // Refresh status
      loadStatus();
    } catch (err: any) {
      const errorMsg = err.response?.data?.error?.message || 'Push to Elation failed';
      toast.error(errorMsg, Helper.bottom_center());
      setPharmacyMessage(errorMsg);
    } finally {
      setPushingPharmacies(false);
    }
  };

  // Render status section
  const renderStatusSection = () => {
    if (!status) return null;

    return (
      <Box mb={2}>
        <Typography variant="subtitle2" gutterBottom>
          Elation Status
        </Typography>
        <Stack direction="row" spacing={1} flexWrap="wrap" mb={1}>
          <Chip
            icon={status.elation_configured ? <CheckIcon /> : <CloseIcon />}
            label={status.elation_configured ? 'API Configured' : 'API Not Configured'}
            color={status.elation_configured ? 'success' : 'error'}
            size="small"
            variant="outlined"
          />
          <Chip
            icon={status.has_elation_patient_id ? <CheckIcon /> : <CloseIcon />}
            label={status.has_elation_patient_id ? `Patient ID: ${status.elation_patient_id}` : 'No Elation Patient ID'}
            color={status.has_elation_patient_id ? 'success' : 'warning'}
            size="small"
            variant="outlined"
          />
        </Stack>

        {!status.elation_configured && (
          <Alert severity="error" sx={{ mb: 1 }}>
            Elation API credentials are not configured. Contact administrator.
          </Alert>
        )}

        {!status.has_elation_patient_id && status.elation_configured && (
          <Alert severity="warning" sx={{ mb: 1 }}>
            Patient does not have an Elation patient ID. Import the patient from Elation first.
          </Alert>
        )}
      </Box>
    );
  };

  // Render care team section
  const renderCareTeamSection = () => {
    if (!status) return null;

    const { care_team } = status;
    const canPush = care_team.can_push;

    return (
      <Card variant="outlined" sx={{ mb: 2 }}>
        <CardContent>
          <Stack direction="row" justifyContent="space-between" alignItems="center" mb={1}>
            <Stack direction="row" spacing={1} alignItems="center">
              <MedicalServicesIcon color="primary" />
              <Typography variant="subtitle1" fontWeight={500}>
                Care Team
              </Typography>
            </Stack>
            <Button
              variant="contained"
              size="small"
              startIcon={pushingCareTeam ? <CircularProgress size={16} /> : <CloudUploadIcon />}
              onClick={handlePushCareTeam}
              disabled={!canPush || pushingCareTeam}
            >
              Push to Elation
            </Button>
          </Stack>

          <Typography variant="body2" color="textSecondary" mb={1}>
            {care_team.validated_with_npi} validated provider(s) with NPI
            {care_team.total_validated > care_team.validated_with_npi && (
              <span> ({care_team.total_validated - care_team.validated_with_npi} without NPI)</span>
            )}
          </Typography>

          {!canPush && (
            <Alert severity="info" sx={{ mt: 1 }}>
              {care_team.validated_with_npi === 0
                ? 'No validated care team members with NPI to push. Validate providers first.'
                : 'Cannot push - check Elation configuration above.'}
            </Alert>
          )}

          {/* Push Results */}
          {careTeamResults && careTeamResults.length > 0 && (
            <Box mt={2}>
              <Divider sx={{ mb: 1 }} />
              <Typography variant="caption" color="textSecondary" gutterBottom>
                Push Results:
              </Typography>
              {careTeamMessage && (
                <Typography variant="body2" fontWeight={500} mb={1}>
                  {careTeamMessage}
                </Typography>
              )}
              <List dense disablePadding>
                {careTeamResults.map((result, idx) => (
                  <ListItem key={idx} disablePadding sx={{ py: 0.25 }}>
                    <ListItemIcon sx={{ minWidth: 28 }}>
                      {result.success ? (
                        <CheckIcon fontSize="small" color="success" />
                      ) : (
                        <CloseIcon fontSize="small" color="error" />
                      )}
                    </ListItemIcon>
                    <ListItemText
                      primary={result.provider_name || `Member ${result.member_id}`}
                      secondary={
                        <span>
                          {result.message}
                          {result.contact_created && ' (New contact created)'}
                          {result.already_on_team && ' (Already on team)'}
                        </span>
                      }
                      primaryTypographyProps={{ variant: 'body2' }}
                      secondaryTypographyProps={{ variant: 'caption' }}
                    />
                  </ListItem>
                ))}
              </List>
            </Box>
          )}
        </CardContent>
      </Card>
    );
  };

  // Render pharmacies section
  const renderPharmaciesSection = () => {
    if (!status) return null;

    const { pharmacies } = status;
    const canPush = pharmacies.can_push;

    return (
      <Card variant="outlined">
        <CardContent>
          <Stack direction="row" justifyContent="space-between" alignItems="center" mb={1}>
            <Stack direction="row" spacing={1} alignItems="center">
              <PharmacyIcon color="primary" />
              <Typography variant="subtitle1" fontWeight={500}>
                Pharmacies
              </Typography>
            </Stack>
            <Button
              variant="contained"
              size="small"
              startIcon={pushingPharmacies ? <CircularProgress size={16} /> : <CloudUploadIcon />}
              onClick={handlePushPharmacies}
              disabled={!canPush || pushingPharmacies}
            >
              Push to Elation
            </Button>
          </Stack>

          <Typography variant="body2" color="textSecondary" mb={1}>
            {pharmacies.with_ncpdp} pharmacy(ies) with NCPDP ID
            {pharmacies.total > pharmacies.with_ncpdp && (
              <span> ({pharmacies.total - pharmacies.with_ncpdp} without NCPDP)</span>
            )}
          </Typography>

          <Typography variant="caption" color="textSecondary" display="block">
            Note: Elation supports up to 2 pharmacy slots. Preferred pharmacy uses Slot 1.
          </Typography>

          {!canPush && (
            <Alert severity="info" sx={{ mt: 1 }}>
              {pharmacies.with_ncpdp === 0
                ? 'No pharmacies with NCPDP IDs to push. Add pharmacies with NCPDP IDs first.'
                : 'Cannot push - check Elation configuration above.'}
            </Alert>
          )}

          {/* Push Results */}
          {pharmacyResults && pharmacyResults.length > 0 && (
            <Box mt={2}>
              <Divider sx={{ mb: 1 }} />
              <Typography variant="caption" color="textSecondary" gutterBottom>
                Push Results:
              </Typography>
              {pharmacyMessage && (
                <Typography variant="body2" fontWeight={500} color="success.main" mb={1}>
                  {pharmacyMessage}
                </Typography>
              )}
              <List dense disablePadding>
                {pharmacyResults.map((pharmacy, idx) => (
                  <ListItem key={idx} disablePadding sx={{ py: 0.25 }}>
                    <ListItemIcon sx={{ minWidth: 28 }}>
                      {pharmacy.is_preferred ? (
                        <StarIcon fontSize="small" sx={{ color: 'gold' }} />
                      ) : (
                        <PharmacyIcon fontSize="small" color="action" />
                      )}
                    </ListItemIcon>
                    <ListItemText
                      primary={
                        <span>
                          Slot {pharmacy.slot}: {pharmacy.name}
                          {pharmacy.is_preferred && (
                            <Chip label="Preferred" size="small" sx={{ ml: 1, height: 18 }} />
                          )}
                        </span>
                      }
                      secondary={`NCPDP: ${pharmacy.ncpdp_id}`}
                      primaryTypographyProps={{ variant: 'body2' }}
                      secondaryTypographyProps={{ variant: 'caption' }}
                    />
                  </ListItem>
                ))}
              </List>
            </Box>
          )}
        </CardContent>
      </Card>
    );
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Stack direction="row" spacing={1} alignItems="center">
          <CloudUploadIcon color="primary" />
          <Typography variant="h6">Push to Elation</Typography>
        </Stack>
      </DialogTitle>
      <DialogContent>
        {error && (
          <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {loading ? (
          <Box display="flex" justifyContent="center" py={4}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            {renderStatusSection()}
            {renderCareTeamSection()}
            {renderPharmaciesSection()}
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
};

export default ElationPushDialog;
